import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
// eslint-disable-next-line css-modules/no-unused-class
import s from './Carousel.scss';
import Clickable from '../Clickable';
import { ICONS } from '../../constants';

const arrowsConfig = {
  small: {
    viewBox: '0 0 20 34',
    width: '1rem',
    height: '1rem',
  },
  large: {
    viewBox: '0 0 20 34',
    width: '1.25rem',
    height: '2.15rem',
  },
};

const Carousel = ({
  autoPlayInterval,
  autoPlay,
  children,
  rootClass,
  dotsClass,
  dots,
  arrows,
  arrowsClass,
  arrowsSize,
  fadeInArrows,
  transition,
  itemsPerSlide,
  slideHeight,
  gapSpacing,
}) => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!autoPlay || children?.length < 1) return null;
    const interval = setInterval(
      () => setCurrent(prevState => (prevState + 1) % children?.length),
      autoPlayInterval,
    );
    return () => clearInterval(interval);
  }, []);

  if (!children) {
    return null;
  }

  const chunks =
    itemsPerSlide > 1
      ? Array.from(
          { length: Math.ceil(children.length / itemsPerSlide) },
          (v, i) =>
            children.slice(
              i * itemsPerSlide,
              i * itemsPerSlide + itemsPerSlide,
            ),
        )
      : children;

  return (
    <div className={cx(s.root, rootClass)}>
      <div className={s.hide}>
        <ul
          className={s.wrapper}
          style={{
            width: `${chunks.length * 100}%`,
          }}
        >
          {chunks.map((chunk, index) => (
            <li
              key={index.toString()}
              className={cx(
                s.item,
                s[transition],
                current === index && s['item--active'],
                itemsPerSlide > 1 && s.itemGrid,
                arrows,
              )}
              style={{
                gridTemplateColumns: `repeat(${itemsPerSlide}, 1fr)`,
                height: slideHeight,
                columnGap: `${gapSpacing}rem`,
              }}
            >
              {chunk}
            </li>
          ))}
        </ul>
      </div>

      {arrows && (
        <div
          className={cx(
            s.arrows,
            arrowsClass === 'basic' ? s.basicArrows : arrowsClass,
          )}
        >
          {current > 0 && (
            <Clickable
              inline
              className={cx(
                s.arrow,
                s.arrow__back,
                fadeInArrows && s.hoverEffect,
              )}
              onClick={() => setCurrent(current - 1)}
              icon={{
                name: ICONS.ARROW_LB_LEFT,
                ...arrowsConfig[arrowsSize],
              }}
              aria-label="Previous slide"
            />
          )}
          {current + 1 < chunks.length && (
            <Clickable
              inline
              className={cx(
                s.arrow,
                s.arrow__next,
                fadeInArrows && s.hoverEffect,
              )}
              onClick={() => setCurrent(current + 1)}
              icon={{
                name: ICONS.ARROW_LB_RIGHT,
                ...arrowsConfig[arrowsSize],
              }}
              aria-label="Next slide"
            />
          )}
        </div>
      )}
      {dots && (
        <div className={cx(s.dots, dotsClass)}>
          {chunks?.map((_, index) => (
            <Clickable
              key={index.toString()}
              className={cx(s.dot, current === index && s['dot--active'])}
              onClick={() => setCurrent(index)}
              aria-label={`Show slide ${index + 1} in carousel`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

Carousel.propTypes = {
  autoPlayInterval: PropTypes.number,
  autoPlay: PropTypes.bool,
  children: PropTypes.node,
  rootClass: PropTypes.string,
  dotsClass: PropTypes.string,
  dots: PropTypes.bool,
  arrows: PropTypes.bool,
  arrowsClass: PropTypes.string,
  fadeInArrows: PropTypes.bool,
  transition: PropTypes.oneOf(['fade', 'slide']),
  itemsPerSlide: PropTypes.number,
  arrowsSize: PropTypes.oneOf(['large', 'small']),
  slideHeight: PropTypes.string,
  gapSpacing: PropTypes.number,
};

Carousel.defaultProps = {
  rootClass: undefined,
  dotsClass: undefined,
  arrowsClass: undefined,
  dots: false,
  arrows: false,
  fadeInArrows: false,
  autoPlay: false,
  autoPlayInterval: 4000,
  children: null,
  transition: 'fade',
  itemsPerSlide: 1,
  arrowsSize: 'large',
  slideHeight: 'auto',
  gapSpacing: 2,
};

export default withStyles(s)(Carousel);
